import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import type { AnalysisResult, PriorReference } from '../lib/types';
import { ScorePill } from './ScorePill';
import { formatScore, formatPct } from '../lib/format';
import styles from './PriorQuarterDiffPanel.module.css';

interface Props {
  current: Pick<AnalysisResult, 'verdict' | 'score' | 'confidence' | 'dimensions' | 'date' | 'title'>;
  prior: PriorReference;
  className?: string;
}

function deltaIcon(delta: number) {
  if (delta > 0.01) return <ArrowUpRight size={14} strokeWidth={2} aria-hidden="true" />;
  if (delta < -0.01) return <ArrowDownRight size={14} strokeWidth={2} aria-hidden="true" />;
  return <Minus size={14} strokeWidth={2} aria-hidden="true" />;
}

function deltaClass(delta: number) {
  if (delta > 0.01) return styles.up;
  if (delta < -0.01) return styles.down;
  return styles.flat;
}

export function PriorQuarterDiffPanel({ current, prior, className }: Props) {
  const scoreDelta = current.score - prior.score;
  const confidenceDelta = current.confidence - prior.confidence;

  const dimensionRows = current.dimensions.map((d) => {
    const priorVal = prior.dimensionScores[d.label];
    const delta = priorVal == null ? 0 : d.value - priorVal;
    return { label: d.label, current: d.value, prior: priorVal, delta, hasPrior: priorVal != null };
  });

  return (
    <div className={[styles.wrap, className].filter(Boolean).join(' ')}>
      <header className={styles.header}>
        <div>
          <span className={styles.eyebrow}>Compared to</span>
          <h3 className={styles.priorTitle}>{prior.title}</h3>
          <p className={styles.priorMeta}>
            <ScorePill verdict={prior.verdict} size="sm" />
            <span className={[styles.priorMetaText, 'mono'].join(' ')}>
              {formatScore(prior.score)} · {formatPct(prior.confidence)}
            </span>
            <span className={styles.priorMetaText}>·</span>
            <time dateTime={prior.date} className={styles.priorMetaText}>
              {new Date(prior.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </time>
          </p>
        </div>
      </header>

      <div className={styles.headlineRow}>
        <div className={styles.bigDelta}>
          <span className={styles.bigDeltaLabel}>Score Δ</span>
          <span className={[styles.bigDeltaValue, deltaClass(scoreDelta), 'mono'].join(' ')}>
            {scoreDelta > 0 ? '+' : ''}{scoreDelta.toFixed(2)}
          </span>
          <span className={styles.bigDeltaNote}>{formatScore(prior.score)} → {formatScore(current.score)}</span>
        </div>
        <div className={styles.bigDelta}>
          <span className={styles.bigDeltaLabel}>Confidence Δ</span>
          <span className={[styles.bigDeltaValue, deltaClass(confidenceDelta / 100), 'mono'].join(' ')}>
            {confidenceDelta > 0 ? '+' : ''}{confidenceDelta.toFixed(0)}pp
          </span>
          <span className={styles.bigDeltaNote}>{prior.confidence}% → {current.confidence}%</span>
        </div>
      </div>

      <ul className={styles.dimList}>
        {dimensionRows.map((r) => (
          <li key={r.label} className={styles.dimRow}>
            <span className={styles.dimLabel}>{r.label}</span>
            <span className={[styles.dimValue, 'mono'].join(' ')}>{r.current.toFixed(2)}</span>
            {r.hasPrior ? (
              <>
                <span className={[styles.dimPrior, 'mono'].join(' ')}>was {r.prior!.toFixed(2)}</span>
                <span className={[styles.dimDelta, deltaClass(r.delta), 'mono'].join(' ')}>
                  {deltaIcon(r.delta)}
                  {r.delta > 0 ? '+' : ''}
                  {r.delta.toFixed(2)}
                </span>
              </>
            ) : (
              <span className={styles.noPrior}>—</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
